import {
  Accordion,
  Button,
  Card,
  colorsTuple,
  createTheme,
  defaultVariantColorsResolver,
  parseThemeColor,
  rem,
  Textarea,
  TextInput,
  Title,
  VariantColorsResolverInput,
} from "@mantine/core";

import * as AccordionStyles from "./styles/components/Accordion.module.css";
import * as ButtonStyles from "./styles/components/Button.module.css";
import * as CardStyles from "./styles/components/Card.module.css";
import * as TextareaStyles from "./styles/components/Textarea.module.css";
import * as TextInputStyles from "./styles/components/TextInput.module.css";

const variantColorResolver = (input: VariantColorsResolverInput) => {
  const { variant, color, theme } = input;
  const defaultResolvedColors = defaultVariantColorsResolver(input);
  const parsedColor = parseThemeColor({
    color: color || theme.primaryColor,
    theme,
  });

  // Handle custom button variants
  if (
    parsedColor.isThemeColor &&
    color === "brandPrimary" &&
    variant.includes("glass")
  ) {
    return {
      ...defaultResolvedColors,
      background: `var(--mantine-color-brandPrimarySecondary-0)`,
      hover: `var(--mantine-color-brandPrimary-0)`,
    };
  }

  return defaultResolvedColors;
};

export const theme = createTheme({
  variantColorResolver,
  colors: {
    // Brand
    brandPrimary: colorsTuple("#B13636"),
    brandPrimarySecondary: colorsTuple("#C33C3C"),
    brandWhite: colorsTuple("#FFFFFF"),
    brandBlack: colorsTuple("#000000"),

    // Layout
    layoutBody: colorsTuple("#FAFAFA"),
    layoutLight: colorsTuple("#E2E2E2"),
    layoutDark: colorsTuple("#121212"),
    layoutDarkSecondary: colorsTuple("#1E1E1E"),

    // Button
    buttonLight: colorsTuple("rgb(0 0 0 / 5%)"),
    buttonLightSecondary: colorsTuple("rgb(255 255 255 / 50%)"),
    buttonDark: colorsTuple("rgb(255 255 255 / 5%)"),

    // Font
    fontLight: colorsTuple("#FFFFFF"),
    fontLightSecondary: colorsTuple("#CFCFCF"),
    fontDark: colorsTuple("#000000"),
    fontDarkSecondary: colorsTuple("#808080"),

    // Decorative
    decorativeBorder: colorsTuple("#BEBEBE"),
    decorativeBorderLight: colorsTuple("rgb(255 255 255 / 10%)"),
    decorativeOverlayDark: colorsTuple("rgb(9 9 9 / 90%)"),
  },
  primaryColor: "brandBlack",
  fontFamily: "Inter, sans-serif",
  fontSizes: {
    xs: rem(16),
    sm: rem(18),
    md: rem(20),
    lg: rem(24),
    xl: rem(32),
  },
  radius: {
    xs: rem(4),
    sm: rem(6),
    md: rem(8),
    lg: rem(12),
    xl: rem(200),
  },
  spacing: {
    xs: rem(4),
    sm: rem(8),
    md: rem(16),
    lg: rem(24),
    xl: rem(32),
    xxl: rem(48),
    xxxl: rem(60),
    breathe: rem(120),
  },
  headings: {
    fontFamily: "Inter, sans-serif",
    fontWeight: "600",
    sizes: {
      h1: {
        fontSize: rem(64),
        fontWeight: "700",
        lineHeight: "1.1",
      },
      h2: {
        fontSize: rem(36),
        lineHeight: "1.25",
      },
      h3: {
        fontSize: rem(32),
        lineHeight: "1.25",
      },
      h4: {
        fontSize: rem(24),
        lineHeight: "1.5",
      },
      h5: {
        fontSize: rem(20),
        lineHeight: "1.5",
      },
      h6: {
        fontSize: rem(18),
        lineHeight: "1.5",
      },
    },
  },
  components: {
    Accordion: Accordion.extend({
      defaultProps: {
        classNames: AccordionStyles,
      },
    }),
    Button: Button.extend({
      defaultProps: {
        classNames: ButtonStyles,
      },
    }),
    Card: Card.extend({
      defaultProps: {
        classNames: CardStyles,
        radius: "md",
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        classNames: TextareaStyles,
      },
    }),
    TextInput: TextInput.extend({
      defaultProps: {
        classNames: TextInputStyles,
      },
    }),
    Title: Title.extend({
      defaultProps: {
        lts: "-0.05em",
      },
    }),
  },
});
